<script lang="ts">
	import {
		createSettingPaymentPeriodValue,
		deleteSettingPaymentPeriodValue
	} from '../../../../api/EnzianSettingsApi';
	import IconButton from '../../../../basic/buttons/IconButton.svelte';
	import DefaultTextInput from '../../../../basic/input/DefaultTextInput.svelte';
	import InputLabel from '../../../../basic/input/InputLabel.svelte';
	import Modal from '../../../../basic/modal/Modal.svelte';
	import ModalHeader from '../../../../basic/modal/ModalHeader.svelte';
	import { addNotification } from '../../../../notification/Notificationstore';
	import { handleStoreError } from '../../../../stores/GeneralStore';
	import type {
		CreateSettingPaymentPeriodValue,
		PatchSettingPaymentPeriod,
		SettingPaymentPeriod,
		SettingPaymentPeriodValue
	} from '../../../../types/BasicTypes';
	import Big from 'big.js';
	import { _ } from 'svelte-i18n';

	import DefaultButton from '../../../../basic/buttons/DefaultButton.svelte';
	import { type SettingPaymentPeriodStore } from '../../../../stores/SettingPaymentPeriodsStore.svelte';
	import SettingPaymentPeriodValueDetail from './SettingPaymentPeriodValueDetail.svelte';

	interface Props {
		store: SettingPaymentPeriodStore;
		onModalClose: () => void;
		settingPaymentPeriod: SettingPaymentPeriod;
	}

	let { settingPaymentPeriod = $bindable(), store, onModalClose }: Props = $props();

	let request: PatchSettingPaymentPeriod = $state({
		defaultName: settingPaymentPeriod.defaultName,
		exportAlias: settingPaymentPeriod.exportAlias
	});

	let createSettingPaymentPeriod: CreateSettingPaymentPeriodValue = {
		name: '',
		days: 0,
		percentage: new Big(0)
	};

	async function handlePatch() {
		try {
			//Send the patch request
			await store.patchSettingPaymentPeriodStore(settingPaymentPeriod.id, request);
			//Hide the modal
			onModalClose();
		} catch (err) {
			handleStoreError('settings.basic.payment_periods.values.err_patch', err);
		}
	}

	async function handleValueDelete(item: SettingPaymentPeriodValue) {
		try {
			//Delete the value
			await deleteSettingPaymentPeriodValue(item.id);

			//We have to remove the value.
			settingPaymentPeriod.settingPaymentPeriodValues =
				settingPaymentPeriod.settingPaymentPeriodValues.filter((val) => val.id != item.id);

			//Show a success notification
			addNotification({
				type: 'success',
				message: $_('settings.basic.payment_periods.values.lbl_delete')
			});
		} catch (error) {
			//Show a error notification
			addNotification({
				type: 'error',
				message: $_('settings.basic.payment_periods.values.err_delete')
			});
		}
	}

	async function handleCreate() {
		//Create the new value
		try {
			//Create the new value
			const addedValue = await createSettingPaymentPeriodValue(
				settingPaymentPeriod.id,
				createSettingPaymentPeriod
			);

			//Add the item to the list
			settingPaymentPeriod.settingPaymentPeriodValues = [
				...settingPaymentPeriod.settingPaymentPeriodValues,
				addedValue
			];

			//Clear the item again
			createSettingPaymentPeriod = {
				name: '',
				days: 0,
				percentage: new Big(0)
			};
		} catch (error) {
			//Show the user that it failed
			handleStoreError('settings.basic.payment_periods.values.err_create_value', error);
		}
	}
</script>

<Modal {onModalClose}>
	{#snippet header()}
		<div>
			<ModalHeader text={$_('settings.basic.payment_periods.lbl_edit')}></ModalHeader>
		</div>
	{/snippet}
	{#snippet content()}
		<div class="flex flex-col p-8">
			<div class="setting-grid items-center gap-2">
				<InputLabel label={$_('settings.basic.payment_periods.lbl_name') + ':'} />
				<DefaultTextInput propertyName="defaultName" bind:value={request.defaultName} />

				<InputLabel label={$_('settings.basic.payment_periods.lbl_vat_alias') + ':'} />
				<DefaultTextInput propertyName="exportAlias" bind:value={request.exportAlias} />
			</div>

			<div
				class="grid grid-cols-3 mt-8 pb-2 border-b-2"
				class:border-b-2={settingPaymentPeriod.settingPaymentPeriodValues.length > 0}
			>
				<InputLabel label={$_('settings.basic.payment_periods.values.lbl_name') + ':'} />

				<InputLabel label={$_('settings.basic.payment_periods.values.lbl_paymnet') + ':'} />

				<InputLabel
					label={$_('settings.basic.payment_periods.values.lbl_percentage_reducation') + ':'}
				/>
			</div>

			{#each settingPaymentPeriod.settingPaymentPeriodValues as settingPaymentPeriodValue}
				<SettingPaymentPeriodValueDetail
					paymentPeriodValue={settingPaymentPeriodValue}
					onDeleteRequest={handleValueDelete}
				/>
			{/each}

			<IconButton
				iconName="plus"
				hoverText={$_('settings.basic.payment_periods.values.bt_add')}
				primary={false}
				onClick={handleCreate}
			/>
		</div>
	{/snippet}
	{#snippet footer()}
		<div>
			<!-- Buttons-->
			<div class="flex justify-end gap-4">
				<DefaultButton
					secondary={true}
					text={$_('general.lbl_cancel')}
					onClick={() => {
						onModalClose();
					}}
				/>
				<DefaultButton spamProtectedCall={handlePatch} text={$_('general.lbl_accept')} />
			</div>
		</div>
	{/snippet}
</Modal>
